/**
 * BrandStrip — "SHOP BY BRAND" horizontally scrollable brand tile row.
 * Maps to the Shop by Brand section in index.html.
 * Fetches from GET /api/get-brands-public.
 * Falls back gracefully to text-only when brandLogo is null.
 * Design tokens: ds-secondary (default text), ds-on-surface (hover text),
 * ds-outline-variant (border), ds-primary (active bottom border),
 * inter (brand name text).
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import SummaryApi from "../common";
import { ROUTES } from "../constants";
import { request } from "../helpers/request";

const BrandTileSkeleton = () => (
  <div className="flex-shrink-0 w-[140px] h-[80px] bg-ds-surface-container rounded-lg animate-pulse" />
);

const BrandStrip = () => {
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const fetchBrands = async () => {
      try {
        const res = await request(SummaryApi.getBrandsPublic);
        const json = await res.json();
        if (!cancelled && json.success) setBrands(json.data);
      } catch {
        // Fail silently — section simply won't render if API is unreachable
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchBrands();
    return () => {
      cancelled = true;
    };
  }, []);

  if (!loading && brands.length === 0) return null;

  return (
    <section className="bg-white py-[80px]" aria-label="Shop by brand">
      <div className="max-w-[1280px] mx-auto px-10">
        <h2 className="font-barlow font-bold text-[36px] text-ds-on-background mb-8 uppercase">
          SHOP BY BRAND
        </h2>

        {/* Horizontally scrollable row, no visible scrollbar */}
        <div
          className="flex gap-4 overflow-x-auto pb-2"
          style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
          role="list"
          aria-label="Brand list"
        >
          {loading
            ? Array.from({ length: 6 }, (_, i) => <BrandTileSkeleton key={i} />)
            : brands.map((brand) => (
                <Link
                  key={brand.slug}
                  to={`${ROUTES.PRODUCT_CATEGORY}?brand=${encodeURIComponent(brand.name)}`}
                  className="flex-shrink-0 w-[140px] h-[80px] bg-white border border-ds-outline-variant/50 rounded-lg flex items-center justify-center group hover:border-b-2 hover:border-b-ds-primary cursor-pointer transition-all duration-200"
                  role="listitem"
                  aria-label={`Shop ${brand.name}`}
                >
                  {brand.logo ? (
                    <img
                      src={brand.logo}
                      alt={brand.name}
                      className="max-h-10 max-w-[110px] object-contain"
                      loading="lazy"
                    />
                  ) : (
                    <span className="font-inter font-semibold text-[14px] text-ds-secondary group-hover:text-ds-on-background transition-colors text-center px-2">
                      {brand.name}
                    </span>
                  )}
                </Link>
              ))}
        </div>

        <div className="mt-8 text-center">
          <Link
            to={`${ROUTES.PRODUCT_CATEGORY}#contact`}
            className="font-inter text-[13px] text-ds-primary uppercase font-bold tracking-wider hover:text-ds-primary-hover transition-colors"
          >
            DON'T SEE YOUR BRAND? CONTACT US →
          </Link>
        </div>
      </div>
    </section>
  );
};

export default BrandStrip;
